import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Package, ChevronRight, AlertTriangle, CheckCircle2,
  Clock, Trash2, Edit3, Eye
} from 'lucide-react';

const PACKAGE_TYPES = [
  { value: 'live_defects', label: 'Live Defects' },
  { value: 'completion', label: 'Completion' },
  { value: 'handover', label: 'Handover' },
  { value: 'sectional', label: 'Sectional' },
  { value: 'final', label: 'Final' },
  { value: 'post_handover', label: 'Post-Handover' },
];

const AREA_TYPES = [
  { value: 'project', label: 'Project' },
  { value: 'block', label: 'Block' },
  { value: 'zone', label: 'Zone' },
  { value: 'floor', label: 'Floor' },
  { value: 'unit', label: 'Unit' },
  { value: 'system', label: 'System' },
  { value: 'external', label: 'External' },
  { value: 'room', label: 'Room' },
];

export default function CloseoutPackages({ projectId, settings }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [form, setForm] = useState({
    package_name: '', package_type: 'live_defects', area_type: 'project',
    area_reference: '', description: '', discipline: '',
    planned_inspection_date: '', planned_handover_date: '',
  });

  const loadPackages = useCallback(async () => {
    try {
      const res = await api.closeout.listPackages(projectId);
      setPackages(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const handleCreate = async () => {
    if (!form.package_name.trim()) { toast.error('Package name required'); return; }
    try {
      await api.closeout.createPackage(projectId, form);
      toast.success('Package created');
      setShowCreate(false);
      setForm({ package_name: '', package_type: 'live_defects', area_type: 'project', area_reference: '', description: '', discipline: '', planned_inspection_date: '', planned_handover_date: '' });
      loadPackages();
    } catch { toast.error('Failed to create package'); }
  };

  const loadPackageDetail = async (pkgId) => {
    try {
      const res = await api.closeout.getPackage(pkgId);
      setSelectedPkg(res.data);
    } catch { toast.error('Failed to load package'); }
  };

  const stageBadgeColor = (stage) => {
    if (['completed_handed_over', 'final_closed'].includes(stage)) return 'bg-green-100 text-green-800';
    if (['defects_outstanding', 'under_rectification'].includes(stage)) return 'bg-amber-100 text-amber-800';
    if (stage === 'draft') return 'bg-slate-100 text-slate-600';
    return 'bg-blue-100 text-blue-800';
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  // Package detail view
  if (selectedPkg) {
    return (
      <div className="space-y-4" data-testid="package-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedPkg(null)}>
            <ChevronRight className="h-4 w-4 rotate-180" /> Back
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-barlow font-bold uppercase">{selectedPkg.package_code} — {selectedPkg.package_name}</h2>
            <p className="text-sm text-slate-500">{selectedPkg.project_name}</p>
          </div>
          <Badge className={stageBadgeColor(selectedPkg.package_stage)}>
            {selectedPkg.stage_label}
          </Badge>
        </div>

        {/* Readiness Meters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{selectedPkg.readiness?.overall || 0}%</p>
              <p className="text-xs text-slate-500 uppercase">Overall Readiness</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{selectedPkg.readiness?.defect_completion || 0}%</p>
              <p className="text-xs text-slate-500 uppercase">Defect Close-Out</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{selectedPkg.readiness?.doc_completion || 0}%</p>
              <p className="text-xs text-slate-500 uppercase">Documents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-700">{selectedPkg.readiness?.signoff_completion || 0}%</p>
              <p className="text-xs text-slate-500 uppercase">Sign-Offs</p>
            </CardContent>
          </Card>
        </div>

        {/* Blockers */}
        {selectedPkg.blockers?.total_count > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <h3 className="text-xs font-bold uppercase text-red-700 flex items-center gap-1.5 mb-2">
                <AlertTriangle className="h-3.5 w-3.5" />
                Why this package cannot be completed ({selectedPkg.blockers.total_count} blocker{selectedPkg.blockers.total_count > 1 ? 's' : ''})
              </h3>
              {selectedPkg.blockers.defect_blockers?.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-red-600 mb-1">Defect Blockers:</p>
                  {selectedPkg.blockers.defect_blockers.map(b => (
                    <div key={b.id} className="text-sm text-red-800 ml-3">
                      {b.defect_no}: {b.description} <Badge variant="outline" className="text-[10px] ml-1">{b.trade}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Defect Summary */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Defect Summary</h3>
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-xl font-bold">{selectedPkg.defect_stats?.total || 0}</p>
                <p className="text-[10px] text-slate-500 uppercase">Total</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-red-600">{selectedPkg.defect_stats?.open || 0}</p>
                <p className="text-[10px] text-slate-500 uppercase">Open</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-green-600">{selectedPkg.defect_stats?.closed || 0}</p>
                <p className="text-[10px] text-slate-500 uppercase">Closed</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-orange-600">{selectedPkg.defect_stats?.blockers || 0}</p>
                <p className="text-[10px] text-slate-500 uppercase">Blockers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Package list view
  return (
    <div className="space-y-4" data-testid="closeout-packages">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Packages</h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-package-btn">
          <Plus className="h-4 w-4" /> New Package
        </Button>
      </div>

      {packages.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">No packages yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first close-out package to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {packages.map(pkg => (
            <Card key={pkg.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadPackageDetail(pkg.id)} data-testid={`package-${pkg.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2 rounded bg-slate-100">
                  <Package className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-xs text-slate-400">{pkg.package_code}</p>
                    <p className="font-medium text-slate-900 truncate">{pkg.package_name}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">{pkg.package_type?.replace(/_/g, ' ')}</Badge>
                    {pkg.area_reference && <span className="text-xs text-slate-400">{pkg.area_reference}</span>}
                    {pkg.discipline && <span className="text-xs text-slate-400">{pkg.discipline}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <Badge className={`text-[10px] ${stageBadgeColor(pkg.package_stage)}`}>{pkg.stage_label}</Badge>
                  <p className="text-xs text-slate-400 mt-1">{pkg.readiness_percent || 0}% ready</p>
                </div>
                {pkg.blocker_count > 0 && (
                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                )}
                <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Package Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">New Close-Out Package</DialogTitle>
          <DialogDescription>Create a package for tracking defects, documents, and handover.</DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Package Name *</Label>
              <Input value={form.package_name} onChange={e => setForm(f => ({...f, package_name: e.target.value}))} placeholder="e.g. Level 3 - HVAC Live Defects" data-testid="pkg-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Package Type</Label>
                <Select value={form.package_type} onValueChange={v => setForm(f => ({...f, package_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PACKAGE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Area Type</Label>
                <Select value={form.area_type} onValueChange={v => setForm(f => ({...f, area_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{AREA_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Area Reference</Label>
                <Input value={form.area_reference} onChange={e => setForm(f => ({...f, area_reference: e.target.value}))} placeholder="e.g. Level 3, Block A" />
              </div>
              <div>
                <Label className="text-xs">Discipline</Label>
                <Input value={form.discipline} onChange={e => setForm(f => ({...f, discipline: e.target.value}))} placeholder="e.g. Multi-trade, HVAC" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Brief package description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-package-submit">
              <Plus className="h-4 w-4 mr-1" /> Create Package
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
